import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery
} from '@mui/material';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';
import axios from 'axios';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';
import { useSharePoint } from '../../contexts/SharePointContext';
import { useFetchGeneration } from '../../hooks/useFetchGeneration';

const ExternalSharingReport = () => {
  const { userSession } = useUserSession();
  const { effectiveConfigId, refreshTrigger } = useSharePoint();
  const { begin, isCurrent } = useFetchGeneration();
  const [libraries, setLibraries] = useState([]);
  const [selectedLibraries, setSelectedLibraries] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [externalShares, setExternalShares] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [reportGenerated, setReportGenerated] = useState(false);

  const fetchExternalShares = async (libraryIds = []) => {
    try {
      const token = authApi.getToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sharepoint/external-shares`,
        { 
          selectedLibraries: libraryIds,
          configId: effectiveConfigId 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        setExternalShares(response.data);
      } else {
        setExternalShares([]);
      }
      setReportGenerated(true);
    } catch (error) {
      console.error('Error fetching external shares:', error.response?.data || error);
      setError('Failed to fetch external shares');
      setExternalShares([]);
    }
  };

  useEffect(() => {
    setSelectedLibraries([]);
    setExternalShares([]);
    setReportGenerated(false);
    if (!userSession?.email || !effectiveConfigId) return;

    const fetchGen = begin();
    const load = async () => {
      try {
        setLoading(true);
        const token = authApi.getToken();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/sharepoint/document-libraries`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { configId: effectiveConfigId }
          }
        );
        if (isCurrent(fetchGen)) {
          setLibraries(response.data);
        }
      } catch (error) {
        if (isCurrent(fetchGen)) {
          console.error('Error fetching document libraries:', error);
          setError('Failed to fetch document libraries');
        }
      } finally {
        if (isCurrent(fetchGen)) {
          setLoading(false);
        }
      }
    };
    load();
  }, [userSession?.email, effectiveConfigId, refreshTrigger, begin, isCurrent]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGenerateReport = async () => {
    if (selectedLibraries.length === 0 || generating) return;

    setGenerating(true);
    setError(null);

    try {
      const token = authApi.getToken();
      
      if (sendEmail) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/sharepoint/external-sharing-report/email`,
          { 
            selectedLibraries,
            configId: effectiveConfigId 
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        );
      } else {
        // First fetch the filtered data to display in the table
        await fetchExternalShares(selectedLibraries);
        
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/sharepoint/external-sharing-report/download`,
          { 
            selectedLibraries,
            configId: effectiveConfigId 
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            responseType: 'blob'
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ExternalSharingReport-${new Date().toLocaleDateString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
        <NavigationDrawer
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          activeSection={''}
          setActiveSection={() => {}}
          navigate={navigate}
        />
        <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            External Sharing Risks Report
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(244, 81, 30, 0.06)' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              What this report is for
            </Typography>
            <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
              This report helps you find content in SharePoint that may be exposed <strong>beyond your organization</strong>:
              for example, items shared with external accounts, guests, or through <strong>anonymous</strong> links. It is
              built from sharing information we have already synced for the site selected in the header.
            </Typography>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              How to run it
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
              <Typography component="li" variant="body2">
                Use the header <strong>Client</strong> and <strong>Site</strong> controls (if shown) so you are on the
                correct SharePoint site before choosing libraries.
              </Typography>
              <Typography component="li" variant="body2">
                Under <strong>Select Document Libraries</strong>, pick one or more libraries to include. Only those
                libraries are searched for this run.
              </Typography>
              <Typography component="li" variant="body2">
                Click <strong>Generate Report</strong> to download an Excel workbook and refresh the{' '}
                <strong>Current External Shares</strong> table on this page with the same scope.
              </Typography>
              <Typography component="li" variant="body2">
                If you turn on <strong>Send report via email</strong>, the workbook is sent to your email instead of
                downloading in the browser; the table below is <strong>not</strong> updated for that email-only run. Turn
                the option off if you want both the download and the on-screen list.
              </Typography>
            </Box>
            <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mt: 1.5 }}>
              Reading the results
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
              <Typography component="li" variant="body2">
                <strong>Shared With</strong> and <strong>Sharing Type</strong> describe who can access the item (for
                example, anonymous links or direct external sharing).
              </Typography>
              <Typography component="li" variant="body2">
                <strong>Permission Level</strong> shows how much access they have (read, edit, and so on). Use this to
                prioritize reviews and tighten or remove sharing where it is no longer needed.
              </Typography>
            </Box>
          </Paper>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Document Libraries</InputLabel>
            <Select
              multiple
              value={selectedLibraries}
              onChange={(e) => setSelectedLibraries(e.target.value)}
              disabled={loading}
            >
              {libraries.map((library) => (
                <MenuItem key={library.id} value={library.id}>
                  {library.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label="Send report via email"
          />

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={selectedLibraries.length === 0 || generating}
              startIcon={generating ? <CircularProgress size={20} /> : null}
            >
              {generating ? 'Generating...' : 'Generate Report'}
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Current External Shares
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Shared By</TableCell>
                  <TableCell>Shared With</TableCell>
                  <TableCell>Sharing Type</TableCell>
                  <TableCell>Permission Level</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : !reportGenerated ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data displayed. Select libraries and click Generate Report to view external shares.
                    </TableCell>
                  </TableRow>
                ) : externalShares.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No external shares found for the selected libraries.
                    </TableCell>
                  </TableRow>
                ) : (
                  externalShares
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((share, index) => (
                      <TableRow key={share.shareId || index}>
                        <TableCell>{share.itemName}</TableCell>
                        <TableCell>{share.itemPath}</TableCell>
                        <TableCell>{share.sharedBy}</TableCell>
                        <TableCell>{share.sharedWith}</TableCell>
                        <TableCell>{share.sharingMethod}</TableCell>
                        <TableCell>{share.permissionLevel}</TableCell>
                        <TableCell>
                          {share.sharedDate && !isNaN(new Date(share.sharedDate)) 
                            ? new Date(share.sharedDate).toLocaleDateString() 
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={externalShares.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default ExternalSharingReport; 